import { Dispatch, MutableRefObject, SetStateAction, useCallback } from "react";
import { useDispatch } from "react-redux";

import actions from "@/actions";
import { MACHINE_ICON_SIZE } from "@/sx-layout/components/plotmap/util/constants";
import { Mark } from "@/sx-layout/models";

type Props = {
  moveable: boolean;
  selectedMark?: Mark;
  updateSelectedMark: Dispatch<SetStateAction<Mark>>;
  markRef: MutableRefObject<HTMLDivElement>;
  imageRef: MutableRefObject<HTMLImageElement>;
  scale: number;
  mapRatio: number;
  setIsDragging: (_: boolean) => void;
};

export const useMoveableMark = ({
  moveable,
  selectedMark,
  updateSelectedMark,
  markRef,
  imageRef,
  scale,
  mapRatio,
  setIsDragging,
}: Props) => {
  const dispatch = useDispatch();

  const onDrag = useCallback(
    ({ beforeTranslate }: { beforeTranslate: number[] }) => {
      // 重機と同じロジック
      if (!moveable) return;

      const target = markRef.current;
      const imageRect = imageRef.current.getBoundingClientRect();

      const targetElem = target;

      // マシーンは中心が基準位置になるため、アイコンの半分サイズ分引く
      const iconSize = MACHINE_ICON_SIZE * (selectedMark.size ?? 1);
      const margin = iconSize / 2;

      // X軸の制限
      let x = beforeTranslate[0];
      const targetLeft = parseInt(targetElem.style.left); // 移動中のオブジェクトのx座標
      const targetWidth = parseInt(targetElem.style.width); // 移動中のオブジェクトの幅
      const imageWidth = imageRect.width * scale; // 画像の幅
      const moveX = targetLeft + beforeTranslate[0] + margin;
      if (moveX < 0) {
        // 画像の左にはみ出さないようにする
        x = -targetLeft - margin;
      } else if (moveX + targetWidth - margin > imageWidth + margin) {
        // 画像の右側にはみ出さないようにする
        x = imageWidth - targetLeft - targetWidth + margin;
      }

      // Y軸の制限
      let y = beforeTranslate[1];
      const targetTop = parseInt(targetElem.style.top); // 移動中のオブジェクトのy座標
      const targetHeight = parseInt(targetElem.style.height); // 移動中のオブジェクトの高さ
      const imageHeight = imageRect.height * scale; // 画像の高さ
      const moveY = targetTop + beforeTranslate[1] + margin;
      if (moveY < 0) {
        // 画像の上にはみ出さないようにする
        y = -targetTop - margin;
      } else if (moveY + targetHeight - margin > imageHeight + margin) {
        // 画面の下にはみ出さないようにする
        y = imageHeight - targetTop - targetHeight + margin;
      }

      setIsDragging(true);
      target.style.transform = `translate(${x}px, ${y}px)`;
    },
    [imageRef, markRef, moveable, scale, setIsDragging, selectedMark]
  );

  const getTranslateXY = (element) => {
    const style = window.getComputedStyle(element);
    const matrix = new DOMMatrixReadOnly(style.transform);

    return {
      translateX: matrix.m41,
      translateY: matrix.m42,
    };
  };

  const onDragEnd = useCallback(
    ({ isDrag }) => {
      if (!moveable) return;

      const target = markRef.current;
      // NOTE: チールチップメニューのクリック等でもonDragEndは発火するが、座標を移動してなければ更新不要
      if (isDrag) {
        setIsDragging(false);
        const { translateX, translateY } = getTranslateXY(target);
        const iconSize = MACHINE_ICON_SIZE * (selectedMark.size ?? 1);
        const markX = Math.round((parseInt(target.style.left) + translateX + iconSize / 2) / mapRatio);
        const markY = Math.round((parseInt(target.style.top) + translateY + iconSize / 2) / mapRatio);

        // 座標の更新
        dispatch(
          actions.mark.updateMarkCoordinates(
            {
              mark_id: selectedMark.mark_id,
              x: markX,
              y: markY,
              timestamp: {
                update_date: selectedMark.timestamp.update_date,
              },
            },
            (response) => {
              target.style.transform = null;
              updateSelectedMark((prev) => ({
                ...prev,
                x: markX,
                y: markY,
                timestamp: {
                  ...selectedMark.timestamp,
                  update_date: response.timestamp.update_date,
                },
              }));
            }
          )
        );
      }
    },
    [
      dispatch,
      markRef,
      mapRatio,
      moveable,
      selectedMark?.mark_id,
      selectedMark?.timestamp,
      setIsDragging,
      updateSelectedMark,
    ]
  );

  return {
    onDrag,
    onDragEnd,
  };
};
